import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "@clerk/clerk-react";
import {
  MdAnalytics,
  MdArrowOutward,
  MdInsights,
  MdLeaderboard,
  MdRefresh,
  MdTouchApp,
} from "react-icons/md";
import styles from "../../../styles/ga4-dashboard.module.scss";
import { getServerURL } from "../../../utils/funcs/get-serverURL";
import { BottomSpacer } from "../../spacer";
import LoadingSpinner from "../../loading-spinner";

type DatePresetKey = "30daysAgo" | "90daysAgo";

interface OverviewCard {
  key: string;
  label: string;
  description: string;
  value: number | string;
  displayValue: string;
}

interface BreakdownRow {
  label: string;
  value: number;
}

interface NamedBreakdown {
  key?: string;
  label?: string;
  eventCount?: number;
  activeUsers?: number;
  sessions?: number;
  screenPageViews?: number;
  totalUsers?: number;
  rows?: BreakdownRow[];
  date?: string;
}

interface TrackedEvent {
  name: string;
  label: string;
  description: string;
  category: string;
  eventCount: number;
  totalUsers: number;
  summary: string;
  breakdowns: Array<{
    key: string;
    label: string;
    rows: BreakdownRow[];
  }>;
  dailyTrend: Array<{
    date: string;
    eventCount: number;
  }>;
}

interface DashboardData {
  success: boolean;
  generatedAt: string;
  propertyId: string;
  dateRange: {
    startDate: string;
    endDate: string;
  };
  overview: {
    activeUsers?: number;
    newUsers?: number;
    sessions?: number;
    engagedSessions?: number;
    eventCount?: number;
    screenPageViews?: number;
    engagementRate?: number;
    averageSessionDuration?: number;
  };
  overviewCards: OverviewCard[];
  events: {
    tracked: TrackedEvent[];
    all: Array<{
      eventName: string;
      eventCount: number;
      totalUsers: number;
      tracked: boolean;
    }>;
    totals: {
      trackedEventCount: number;
      allEventCount: number;
    };
  };
  breakdowns: {
    dailyTrend: NamedBreakdown[];
    topPages: NamedBreakdown[];
    trafficChannels: NamedBreakdown[];
    countries: NamedBreakdown[];
    devices: NamedBreakdown[];
  };
  metadata: {
    availableTrackedCustomDimensions: Array<{ key: string; label: string }>;
    missingTrackedCustomDimensions: Array<{ key: string; label: string }>;
  };
}

const DATE_PRESETS: Array<{
  key: DatePresetKey;
  label: string;
  helper: string;
}> = [
  { key: "30daysAgo", label: "Last 30 days", helper: "Best for current demand and intent" },
  { key: "90daysAgo", label: "Last 90 days", helper: "Better for spotting wider trends" },
];

function formatWholeNumber(value: number | string | undefined) {
  return Number(value || 0).toLocaleString("en-GB", { maximumFractionDigits: 0 });
}

function formatOneDecimal(value: number) {
  return value.toLocaleString("en-GB", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
}

function pickPlural(count: number, singular: string, plural: string) {
  return count === 1 ? singular : plural;
}

function getTrackedEvent(data: DashboardData | null, eventName: string) {
  return data?.events.tracked.find((eventItem) => eventItem.name === eventName) || null;
}

function isMeaningfulBreakdownLabel(label: string) {
  const normalisedLabel = label.trim().toLowerCase();

  if (!normalisedLabel) {
    return false;
  }

  return (
    !normalisedLabel.startsWith("unknown ") &&
    normalisedLabel !== "(not set)" &&
    normalisedLabel !== "not set"
  );
}

function getPrimaryBreakdown(eventItem: TrackedEvent) {
  const rankedBreakdowns = [...eventItem.breakdowns].sort((leftBreakdown, rightBreakdown) => {
    const leftMeaningfulRows = leftBreakdown.rows.filter((row) => isMeaningfulBreakdownLabel(row.label));
    const rightMeaningfulRows = rightBreakdown.rows.filter((row) => isMeaningfulBreakdownLabel(row.label));
    const leftMeaningfulValue = leftMeaningfulRows.reduce((total, row) => total + Number(row.value || 0), 0);
    const rightMeaningfulValue = rightMeaningfulRows.reduce((total, row) => total + Number(row.value || 0), 0);

    if (rightMeaningfulValue !== leftMeaningfulValue) {
      return rightMeaningfulValue - leftMeaningfulValue;
    }

    return rightMeaningfulRows.length - leftMeaningfulRows.length;
  });

  const preferredBreakdown = rankedBreakdowns.find((breakdown) =>
    breakdown.rows.some((row) => isMeaningfulBreakdownLabel(row.label)),
  );

  return preferredBreakdown || rankedBreakdowns[0] || null;
}

function getTrafficSourceDescription(label: string) {
  const sourceDescriptions: Record<string, string> = {
    Direct: "People arrived by typing the website address directly or using a saved bookmark.",
    Organic: "People found the site through unpaid search engine results.",
    "Organic Search": "People found the site through unpaid search engine results.",
    Referral: "People clicked through from another website that linked to Teck Engineers.",
    Paid: "People arrived from paid advertising campaigns.",
    "Paid Search": "People arrived from paid search ads.",
    Email: "People clicked through from an email campaign or newsletter.",
    Social: "People arrived from social media platforms.",
    "Organic Social": "People arrived from unpaid social posts or shares.",
    "Paid Social": "People arrived from paid social advertising.",
    Display: "People arrived from display ads placed across the web.",
    Video: "People arrived from video-based campaigns or platforms.",
    "Cross-network": "People arrived from campaigns that ran across multiple Google channels.",
    Affiliates: "People arrived through affiliate partner links.",
    Audio: "People arrived from audio advertising campaigns.",
    SMS: "People arrived from tracked SMS links.",
    Mobile: "People arrived from mobile app or mobile-specific campaign sources.",
    "Unassigned": "GA4 could not confidently classify the source for these visits.",
    "Unattributed traffic": "GA4 did not assign a clear acquisition source to these visits.",
  };

  return sourceDescriptions[label] || "This is a GA4 traffic bucket showing where these visits were grouped from.";
}

function buildMeaningSummary(data: DashboardData | null) {
  if (!data) {
    return [] as string[];
  }

  const activeUsers = Number(data.overview.activeUsers || 0);
  const bookNowUsers = Number(getTrackedEvent(data, "book_now_click")?.totalUsers || 0);
  const paymentUsers = Number(getTrackedEvent(data, "proceed_to_payment_click")?.totalUsers || 0);
  const scroll50Users = Number(getTrackedEvent(data, "scroll_50")?.totalUsers || 0);
  const scroll90Users = Number(getTrackedEvent(data, "scroll_90")?.totalUsers || 0);

  const summaries: string[] = [];

  if (activeUsers > 0) {
    summaries.push("People are reaching the site and browsing through multiple pages.");
  }

  if (scroll50Users > 0 && scroll90Users < scroll50Users) {
    summaries.push("Engagement drops as visitors move deeper into page content.");
  } else if (scroll90Users > 0) {
    summaries.push("A healthy share of visitors are making it deep into page content.");
  }

  if (bookNowUsers === 0 && paymentUsers === 0) {
    summaries.push("Visitors are browsing, but there is currently no strong booking intent in this time window.");
  } else if (bookNowUsers > 0 && paymentUsers === 0) {
    summaries.push("Some visitors are showing intent by clicking booking calls to action, but they are not yet moving on to payment.");
  } else if (paymentUsers > 0) {
    summaries.push("A portion of visitors are progressing beyond browsing and into the checkout journey.");
  }

  return summaries;
}

function getRangeTitle(preset: DatePresetKey) {
  return preset === "90daysAgo" ? "Last 90 Days" : "Last 30 Days";
}

export default function GoogleAnalyticsDashboard() {
  const { getToken } = useAuth();
  const serverURL = getServerURL();

  const [activePreset, setActivePreset] = useState<DatePresetKey>("30daysAgo");
  const [showDebug, setShowDebug] = useState(false);
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getAuthHeader = useCallback(async () => {
    const token = await getToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, [getToken]);

  const fetchAnalytics = useCallback(
    async (preset: DatePresetKey, showRefreshState = false) => {
      try {
        setError(null);
        if (showRefreshState) {
          setRefreshing(true);
        } else {
          setLoading(true);
        }

        const headers = await getAuthHeader();
        const response = await axios.get<DashboardData>(`${serverURL}/admin/analytics/ga4`, {
          headers,
          params: {
            startDate: preset,
            endDate: "today",
          },
        });

        if (response.data.success) {
          setData(response.data);
        } else {
          setError("Failed to load Google Analytics data.");
        }
      } catch (err) {
        if (axios.isAxiosError(err)) {
          setError(err.response?.data?.error || "Error loading Google Analytics data from the server.");
        } else {
          setError("Error loading Google Analytics data from the server.");
        }
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [getAuthHeader, serverURL],
  );

  useEffect(() => {
    fetchAnalytics(activePreset);
  }, [activePreset, fetchAnalytics]);

  const activeUsers = Number(data?.overview.activeUsers || 0);
  const sessions = Number(data?.overview.sessions || 0);
  const pageViews = Number(data?.overview.screenPageViews || 0);
  const pagesPerVisitor = activeUsers > 0 ? pageViews / activeUsers : 0;
  const scroll50 = getTrackedEvent(data, "scroll_50");
  const scroll90 = getTrackedEvent(data, "scroll_90");
  const serviceClick = getTrackedEvent(data, "service_click");
  const bookNow = getTrackedEvent(data, "book_now_click");
  const paymentClick = getTrackedEvent(data, "proceed_to_payment_click");
  const paymentSuccess = getTrackedEvent(data, "payment_success");
  const meaningSummary = buildMeaningSummary(data);
  const serviceClickPrimaryBreakdown = serviceClick ? getPrimaryBreakdown(serviceClick) : null;
  const topPages = data?.breakdowns.topPages.slice(0, 4) || [];
  const channels = data?.breakdowns.trafficChannels.slice(0, 4) || [];
  const devices = data?.breakdowns.devices.slice(0, 3) || [];
  const headlineCards = data?.overviewCards.slice(0, 6) || [];
  const trackedEvents = data?.events.tracked.filter((eventItem) => eventItem.eventCount > 0) || [];
  const maxTopPageValue = Math.max(...topPages.map((item) => Number(item.screenPageViews || 0)), 1);
  const maxChannelValue = Math.max(...channels.map((item) => Number(item.sessions || 0)), 1);
  const serviceClickDebugPayload = serviceClick
    ? {
        eventName: serviceClick.name,
        label: serviceClick.label,
        totalUsers: serviceClick.totalUsers,
        eventCount: serviceClick.eventCount,
        primaryBreakdownKey: serviceClickPrimaryBreakdown?.key || null,
        primaryBreakdownLabel: serviceClickPrimaryBreakdown?.label || null,
        availableBreakdowns: serviceClick.breakdowns.map((breakdown) => ({
          key: breakdown.key,
          label: breakdown.label,
          rows: breakdown.rows,
        })),
        availableTrackedCustomDimensions: data?.metadata.availableTrackedCustomDimensions || [],
        missingTrackedCustomDimensions: data?.metadata.missingTrackedCustomDimensions || [],
      }
    : null;

  if (loading) {
    return (
      <div className={styles.stateCard}>
        <div className={styles.loadingStateContent}>
          <LoadingSpinner />
          <p>Loading Google Analytics…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.stateCard}>
        <h2>Google Analytics is unavailable</h2>
        <p>{error}</p>
        <button className={styles.retryButton} onClick={() => fetchAnalytics(activePreset, true)}>
          <MdRefresh />
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className={styles.dashboard}>
      <section className={styles.hero}>
        <div>
          <p className={styles.eyebrow}>Google Analytics</p>
          <h2 className={styles.title}>Teck Engineers Website Activity</h2>
          <p className={styles.subtitle}>
            A plain-language view of traffic, engagement, and booking intent pulled directly from GA4.
          </p>
          <div className={styles.heroMeta}>
            <span>
              <MdAnalytics />
              {getRangeTitle(activePreset)}
            </span>
            <span>
              <MdInsights />
              Updated {data?.generatedAt ? new Date(data.generatedAt).toLocaleString("en-GB") : "just now"}
            </span>
          </div>
        </div>

        <div className={styles.toolbar}>
          <div className={styles.presetGroup}>
            {DATE_PRESETS.map((preset) => (
              <button
                key={preset.key}
                className={activePreset === preset.key ? styles.presetButtonActive : styles.presetButton}
                onClick={() => setActivePreset(preset.key)}
              >
                <strong>{preset.label}</strong>
                <span>{preset.helper}</span>
              </button>
            ))}
          </div>

          <button className={styles.refreshButton} onClick={() => fetchAnalytics(activePreset, true)}>
            <MdRefresh className={refreshing ? styles.spin : ""} />
            {refreshing ? "Refreshing…" : "Refresh data"}
          </button>
        </div>
      </section>

      <section className={styles.storyGrid}>
        <article className={styles.storyCard}>
          <div className={styles.storyIcon}><MdLeaderboard /></div>
          <h3>Overall Traffic</h3>
          <p>
            <strong>{formatWholeNumber(sessions)}</strong> visits from users and <strong>{formatWholeNumber(pageViews)}</strong> total page views.
          </p>
          <p>
            Each visitor viewed about <strong>{formatOneDecimal(pagesPerVisitor)}</strong> {pickPlural(Math.round(pagesPerVisitor || 0), "page", "pages")} on average.
          </p>
        </article>

        <article className={styles.storyCard}>
          <div className={styles.storyIcon}><MdArrowOutward /></div>
          <h3>How Far People Are Engaging</h3>
          <p>
            <strong>{formatWholeNumber(scroll50?.totalUsers)}</strong> people scrolled at least halfway down a page.
          </p>
          <p>
            <strong>{formatWholeNumber(scroll90?.totalUsers)}</strong> people made it most of the way through the content.
          </p>
          <p className={styles.hintText}>
            {Number(scroll50?.totalUsers || 0) > Number(scroll90?.totalUsers || 0)
              ? "Most users are interested, but fewer are reaching the deepest content."
              : "Visitors who start engaging are generally continuing deep into the page."}
          </p>
        </article>

        <article className={styles.storyCard}>
          <div className={styles.storyIcon}><MdTouchApp /></div>
          <h3>What People Showed Interest In</h3>
          <p>
            <strong>{formatWholeNumber(serviceClick?.totalUsers)}</strong> people explored a specific service.
          </p>
          <p>
            <strong>{formatWholeNumber(bookNow?.totalUsers)}</strong> people clicked Book Now, and <strong>{formatWholeNumber(paymentClick?.totalUsers)}</strong> moved toward payment.
          </p>
          <p className={styles.hintText}>
            {Number(bookNow?.totalUsers || 0) > 0
              ? "A small number of visitors are showing clear booking intent."
              : "Browsing activity exists, but booking intent is still weak in this period."}
          </p>
        </article>

        <article className={styles.storyCardAccent}>
          <h3>What This Means</h3>
          {meaningSummary.length > 0 ? (
            <div className={styles.meaningList}>
              {meaningSummary.map((line) => (
                <p key={line}>{line}</p>
              ))}
            </div>
          ) : (
            <p>GA4 has loaded, but there is not enough recent activity yet to generate a useful interpretation.</p>
          )}
          <div className={styles.accentFooter}>
            Successful payments tracked: <strong>{formatWholeNumber(paymentSuccess?.totalUsers)}</strong>
          </div>
        </article>
      </section>

      <section className={styles.kpiSection}>
        {headlineCards.map((card) => (
          <article key={card.key} className={styles.kpiCard}>
            <p className={styles.kpiLabel}>{card.label}</p>
            <h3>{card.displayValue}</h3>
            <p>{card.description}</p>
          </article>
        ))}
      </section>

      <section className={styles.panelGrid}>
        <article className={styles.panel}>
          <div className={styles.panelHeader}>
            <div>
              <p className={styles.panelEyebrow}>Traffic Sources</p>
              <h3>How visitors are finding the site</h3>
            </div>
          </div>

          <div className={styles.barList}>
            {channels.length > 0 ? (
              channels.map((item) => {
                const sessionsValue = Number(item.sessions || 0);
                const width = `${Math.max((sessionsValue / maxChannelValue) * 100, 8)}%`;

                return (
                  <div key={item.label} className={styles.barRow}>
                    <div className={styles.barLabels}>
                      <span>{item.label || "(not set)"}</span>
                      <strong>{formatWholeNumber(sessionsValue)} sessions</strong>
                    </div>
                    <p className={styles.barDescription}>{getTrafficSourceDescription(item.label || "Unattributed traffic")}</p>
                    <div className={styles.barTrack}>
                      <div className={styles.barFill} style={{ width }} />
                    </div>
                  </div>
                );
              })
            ) : (
              <p className={styles.emptyText}>No channel data returned for this period.</p>
            )}
          </div>
        </article>

        <article className={styles.panel}>
          <div className={styles.panelHeader}>
            <div>
              <p className={styles.panelEyebrow}>Top Pages</p>
              <h3>Where attention is concentrating</h3>
            </div>
          </div>

          <div className={styles.barList}>
            {topPages.length > 0 ? (
              topPages.map((item) => {
                const pageViewsValue = Number(item.screenPageViews || 0);
                const width = `${Math.max((pageViewsValue / maxTopPageValue) * 100, 8)}%`;

                return (
                  <div key={item.label} className={styles.barRow}>
                    <div className={styles.barLabels}>
                      <span>{item.label || "/"}</span>
                      <strong>{formatWholeNumber(pageViewsValue)} views</strong>
                    </div>
                    <div className={styles.barTrack}>
                      <div className={styles.barFillAlt} style={{ width }} />
                    </div>
                  </div>
                );
              })
            ) : (
              <p className={styles.emptyText}>No page view data returned for this period.</p>
            )}
          </div>
        </article>
      </section>

      <section className={styles.panelGrid}>
        <article className={styles.panel}>
          <div className={styles.panelHeader}>
            <div>
              <p className={styles.panelEyebrow}>Tracked Events</p>
              <h3>Actions that matter most</h3>
            </div>
            <button
              type="button"
              className={styles.debugToggle}
              onClick={() => setShowDebug((currentValue) => !currentValue)}
            >
              {showDebug ? "Hide debug" : "Show debug"}
            </button>
          </div>

          <div className={styles.eventList}>
            {trackedEvents.length > 0 ? (
              trackedEvents.map((eventItem) => {
                const primaryBreakdown = getPrimaryBreakdown(eventItem);

                return (
                  <div key={eventItem.name} className={styles.eventCard}>
                    <div className={styles.eventHeader}>
                      <div>
                        <p className={styles.eventCategory}>{eventItem.category}</p>
                        <h4>{eventItem.label}</h4>
                      </div>
                      <div className={styles.eventMetrics}>
                        <span>{formatWholeNumber(eventItem.totalUsers)} users</span>
                        <strong>{formatWholeNumber(eventItem.eventCount)} events</strong>
                      </div>
                    </div>
                    <p className={styles.eventSummary}>{eventItem.summary}</p>
                    {primaryBreakdown && primaryBreakdown.rows.length > 0 ? (
                      <div className={styles.chipRow}>
                        {primaryBreakdown.rows.slice(0, 4).map((row) => (
                          <span key={`${eventItem.name}-${row.label}`} className={styles.chip}>
                            {row.label}: {formatWholeNumber(row.value)}
                          </span>
                        ))}
                      </div>
                    ) : null}
                  </div>
                );
              })
            ) : (
              <p className={styles.emptyText}>No tracked events were recorded in this period.</p>
            )}
          </div>

          {showDebug ? (
            <div className={styles.debugBox}>
              <h4>Service click debug</h4>
              <p>
                This shows the exact `service_click` payload the dashboard is receiving after the GA4 endpoint has already processed it.
              </p>

              {serviceClickDebugPayload ? (
                <>
                  <div className={styles.debugMetaGrid}>
                    <div className={styles.debugMetaCard}>
                      <span>Primary breakdown</span>
                      <strong>{serviceClickDebugPayload.primaryBreakdownKey || "None"}</strong>
                    </div>
                    <div className={styles.debugMetaCard}>
                      <span>Users</span>
                      <strong>{formatWholeNumber(serviceClickDebugPayload.totalUsers)}</strong>
                    </div>
                    <div className={styles.debugMetaCard}>
                      <span>Events</span>
                      <strong>{formatWholeNumber(serviceClickDebugPayload.eventCount)}</strong>
                    </div>
                  </div>

                  <pre className={styles.debugPre}>
                    {JSON.stringify(serviceClickDebugPayload, null, 2)}
                  </pre>
                </>
              ) : (
                <p className={styles.emptyText}>No `service_click` event data was returned for this date range.</p>
              )}
            </div>
          ) : null}
        </article>

        <article className={styles.panel}>
          <div className={styles.panelHeader}>
            <div>
              <p className={styles.panelEyebrow}>Audience Snapshot</p>
              <h3>Devices and data coverage</h3>
            </div>
          </div>

          <div className={styles.deviceGrid}>
            {devices.map((device) => (
              <div key={device.label} className={styles.deviceCard}>
                <span>{device.label || "Unknown device"}</span>
                <strong>{formatWholeNumber(device.activeUsers)} users</strong>
              </div>
            ))}
          </div>

          {data?.metadata.missingTrackedCustomDimensions.length ? (
            <div className={styles.noteBox}>
              <h4>Missing GA4 custom dimensions</h4>
              <p>
                These event parameters are being tracked in the app, but GA4 is not exposing them yet for reporting.
              </p>
              <div className={styles.chipRow}>
                {data.metadata.missingTrackedCustomDimensions.map((dimension) => (
                  <span key={dimension.key} className={styles.chipMuted}>{dimension.label}</span>
                ))}
              </div>
            </div>
          ) : (
            <div className={styles.noteBoxSuccess}>
              <h4>Tracked parameter coverage is healthy</h4>
              <p>GA4 is exposing the custom dimensions needed for the tracked event breakdowns shown here.</p>
            </div>
          )}
        </article>
      </section>
      <BottomSpacer />
    </div>
  );
}